/**
 * ============================================================================
 * WINDOW STATE PERSISTENCE
 * ============================================================================
 *
 * Handles persisting and restoring window state (open/closed/minimized,
 * position, size, z-index) to localStorage across page refreshes.
 *
 * @packageDocumentation
 */

import { scopedKey } from "../lib/scopedStorage";

const STORAGE_KEY_BASE = "desktopUI:windowState";
const storageKey = () => scopedKey(STORAGE_KEY_BASE);

/**
 * Persisted geometry for a window.
 */
export interface PersistedWindowGeometry {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Persisted state for a single window.
 */
export interface PersistedWindowState {
  status: "closed" | "open" | "minimized";
  isActive?: boolean;
  isMaximized?: boolean;
  zIndex?: number;
  // Current geometry
  position?: { x: number; y: number };
  size?: { width: number; height: number };
  // Saved geometry for restore from maximized/snapped
  savedPosition?: { x: number; y: number };
  savedSize?: { width: number; height: number };
  previousGeometry?: PersistedWindowGeometry;
  isSnapped?: "left" | "right" | null;
}

/**
 * Full persisted desktop state.
 */
export interface PersistedDesktopState {
  windows: Record<string, PersistedWindowState>;
  nextZIndex: number;
}

/**
 * Save the complete desktop state to localStorage.
 */
export function saveDesktopState(state: PersistedDesktopState): void {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(state));
  } catch (e) {
    console.warn("Failed to save desktop state:", e);
  }
}

/**
 * Load the desktop state from localStorage.
 * Returns null if no state is saved or if parsing fails.
 */
export function loadDesktopState(): PersistedDesktopState | null {
  // Guard against SSR where localStorage doesn't exist
  if (typeof window === "undefined") return null;

  try {
    const stored = localStorage.getItem(storageKey());
    if (!stored) return null;
    return JSON.parse(stored) as PersistedDesktopState;
  } catch (e) {
    console.warn("Failed to load desktop state:", e);
    return null;
  }
}

/**
 * Clear the persisted desktop state.
 */
export function clearDesktopState(): void {
  try {
    localStorage.removeItem(storageKey());
  } catch (e) {
    console.warn("Failed to clear desktop state:", e);
  }
}

/**
 * Check if there is persisted state available.
 */
export function hasPersistedDesktopState(): boolean {
  try {
    return localStorage.getItem(storageKey()) !== null;
  } catch {
    return false;
  }
}

/**
 * Update a single window's state in storage.
 * This is a convenience function for partial updates.
 */
export function updateWindowState(
  windowId: string,
  updates: Partial<PersistedWindowState>
): void {
  const current = loadDesktopState();
  if (!current) return;

  const windowState = current.windows[windowId] ?? { status: "closed" };
  current.windows[windowId] = { ...windowState, ...updates };
  saveDesktopState(current);
}

/**
 * Debounce utility for reducing storage writes.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounce<T extends (...args: any[]) => void>(
  fn: T,
  ms: number
): T {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  return ((...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), ms);
  }) as T;
}
